/**
 * ClienteHospitalFormatInfoModal - Modal with Excel format information
 *
 * Shows the expected columns and format for importing cliente-hospital relationships
 * Supports multiple clients and hospitals in the same file
 */
import React from 'react';
import { Info, X, AlertTriangle } from 'lucide-react';

interface ClienteHospitalFormatInfoModalProps {
  show: boolean;
  onClose: () => void;
}

export const ClienteHospitalFormatInfoModal: React.FC<ClienteHospitalFormatInfoModalProps> = ({
  show,
  onClose,
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4">
        {/* Header - Green */}
        <div className="flex items-center justify-between p-4 bg-green-600 rounded-t-lg">
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-white" />
            <h3 className="text-lg font-semibold text-white">
              Formato do Excel - Clientes-Hospitais
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-green-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Overwrite Warning */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-semibold text-amber-800 dark:text-amber-200">
                  Comportamento de Sobrescrita
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  Se um vínculo com o <strong>mesmo par Cliente-Hospital</strong> já existir,
                  ele será <strong>SOBRESCRITO</strong> com os novos dados do Excel.
                </p>
              </div>
            </div>
          </div>

          {/* Multi-client info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              💡 <strong>Múltiplos clientes:</strong> O arquivo pode conter vínculos para
              <strong> vários clientes</strong> e <strong>vários hospitais</strong> de uma só vez.
            </p>
          </div>

          {/* Columns Section */}
          <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              📋 Colunas do Excel
            </p>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400 w-6">A:</span>
                <span className="text-gray-800 dark:text-gray-200">
                  Codigo_Cliente_Hospital <span className="text-red-500 font-bold">*</span>
                </span>
                <span className="text-xs text-gray-500">(código único por par)</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400 w-6">B:</span>
                <span className="text-gray-800 dark:text-gray-200">
                  CNPJ_Cliente <span className="text-red-500 font-bold">*</span>
                </span>
                <span className="text-xs text-gray-500">(deve existir no sistema)</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400 w-6">C:</span>
                <span className="text-gray-800 dark:text-gray-200">
                  Nome_Cliente
                </span>
                <span className="text-xs text-gray-500">(informativo)</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400 w-6">D:</span>
                <span className="text-gray-800 dark:text-gray-200">
                  CNPJ_Hospital <span className="text-red-500 font-bold">*</span>
                </span>
                <span className="text-xs text-gray-500">(deve existir no sistema)</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400 w-6">E:</span>
                <span className="text-gray-800 dark:text-gray-200">
                  Nome_Hospital
                </span>
                <span className="text-xs text-gray-500">(informativo)</span>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-3">
            <p><span className="text-red-500 font-bold">*</span> Campos obrigatórios</p>
            <p className="mt-1">💡 <strong>Dica:</strong> Baixe o template clicando em "Baixar Template" para ver exemplos.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};
